import { useState, useMemo } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import HeroSection from './components/HeroSection';
import CategoryNav from './components/CategoryNav';
import PopularManga from './components/PopularManga';
import LatestUpdates from './components/LatestUpdates';
import FAQSection from './components/FAQSection';
import MangaCard from '@/components/base/MangaCard';
import { mangaList, categories, latestUpdates, faqData } from '@/mocks/mangaData';

const homeJsonLd = [
  {
    '@type': 'Organization',
    name: '刃牙漫画',
    url: 'https://qnzodog.cn',
    description: '刃牙漫画免费全集在线阅读平台，下拉式阅读，全部章节一次畅读',
  },
  {
    '@type': 'WebSite',
    name: '刃牙漫画',
    url: 'https://qnzodog.cn',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://qnzodog.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@type': 'WebPage',
    name: '刃牙漫画 - 免费全集在线阅读首页',
    description: '刃牙漫画免费全集在线阅读，下拉式即时阅读体验，全部章节免费畅读',
  },
];

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const displayManga = useMemo(() => {
    if (activeCategory === 'all') return mangaList;
    if (activeCategory === 'popular') return mangaList.filter((m) => m.rating >= 9.2);
    if (activeCategory === 'completed') return mangaList.filter((m) => m.status === '已完结');
    if (activeCategory === 'fighting') return mangaList.filter((m) => m.category === '格斗');
    if (activeCategory === 'gaiden') return mangaList.filter((m) => m.category === '外传');
    if (activeCategory === 'special') return mangaList.filter((m) => m.category === '特别篇');
    return mangaList;
  }, [activeCategory]);

  return (
    <>
      <SEOHead
        title="刃牙漫画 - 刃牙漫画免费全集在线阅读 | 免费看刃牙漫画下拉式完整版"
        description="免费看完整部刃牙漫画，无需充值，下拉式即时阅读。刃牙漫画免费全集在线观看，全部章节一次畅读。刃牙漫画免费,免费看刃牙,刃牙漫画免费全集,刃牙漫画免费阅读,刃牙漫画在线观看,刃牙漫画下拉式,刃牙漫画全集,刃牙漫画大全"
        keywords="刃牙漫画免费,免费看刃牙,刃牙漫画免费全集,刃牙漫画免费阅读,刃牙漫画在线观看,刃牙漫画下拉式,刃牙漫画全集,刃牙漫画大全"
        canonicalUrl="https://qnzodog.cn"
        jsonLd={homeJsonLd}
      />

      <main>
        <HeroSection />

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  漫画分类
                </h2>
              </div>
              <CategoryNav activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {displayManga.map((manga) => (
                  <MangaCard key={manga.id} {...manga} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  <strong>热门推荐</strong>
                </h2>
                <a
                  href="/category/popular"
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </a>
              </div>
              <PopularManga />
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="flex flex-col gap-5">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                      最新更新
                    </h2>
                    <a
                      href="/category/all"
                      className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                    >
                      全部更新 <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                  <LatestUpdates />
                </div>
              </div>

              <div>
                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                    阅读指南
                  </h3>
                  <ol className="space-y-2.5">
                    {[
                      { num: '01', title: '刃牙（第一部）', desc: '地下格斗篇' },
                      { num: '02', title: '刃牙II', desc: '最凶死囚篇' },
                      { num: '03', title: '范马刃牙', desc: '父子对决篇' },
                      { num: '04', title: '刃牙道', desc: '终极对决篇' },
                      { num: '05', title: '刃牙道II', desc: '宫本武藏篇' },
                    ].map((item) => (
                      <li key={item.num} className="flex items-start gap-3">
                        <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-xs font-bold">
                          {item.num}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{item.title}</p>
                          <p className="text-xs text-foreground-500">{item.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col gap-8">
              <div className="text-center">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">关于刃牙漫画免费阅读的常见疑问</p>
              </div>
              <FAQSection faqs={faqData} />
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-primary-500">
          <div className="max-w-3xl mx-auto text-center px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              立即开始免费阅读刃牙漫画
            </h2>
            <p className="text-background-50/80 text-sm md:text-base mb-6">
              全部系列、全部章节免费开放，无需注册，即刻畅读
            </p>
            <a
              href="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3 bg-background-50 text-primary-600 rounded-lg text-base font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              开始阅读
            </a>
          </div>
        </section>
      </main>
    </>
  );
}